const { getElement } = window.BAO.Utils

export class ProductForm extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.form, 'submit', this.onSubmitHandler.bind(this))
    this.listeners.add(
      document.body,
      'product-engraving:set',
      this.onEngravingSetHandler.bind(this),
    )
    this.listeners.add(
      document.body,
      'product-foiling:set',
      this.onFoilingSetHandler.bind(this),
    )
    this.listeners.add(
      document.body,
      'product-fixed-bar:quantity-change',
      this.onExternalQuantityChange.bind(this),
    )

    if (this.externalEls['single-pair-selector']) {
      this.onExternalQuantityChange = this.onExternalQuantityChange.bind(this)

      this.externalEls['single-pair-selector'].elements.forEach(selector => {
        this.listeners.add(
          selector,
          'single-pair-selector:quantity-change',
          this.onExternalQuantityChange,
        )
      })
    }
  }

  checkValidity () {
    super.checkValidity()

    if (!this.form) {
      this.throwError(`needs to be wrapped around a product's <form> element`)
    }
  }

  /**
   * @param {CustomEvent<{
   *   font: string;
   *   text: string;
   *   formId: string;
   * }>} event
   */
  onEngravingSetHandler (event) {
    const { font, text, formId } = event.detail

    if (this.form.getAttribute('id') !== formId) {
      return
    }

    const {
      'engraving-text': engravingTextInput,
      'engraving-font': engravingFontInput,
    } = this.els

    if (!(engravingTextInput && engravingFontInput)) {
      return
    }

    text.forEach(text => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = `items[0][properties][Engraving_${text[0]}]`
      input.value = text[1]
      engravingTextInput.element.appendChild(input) // put it into the DOM
    })

    engravingFontInput.element.value = font
  }

  /**
   * @param {CustomEvent<{
   *   font: string;
   *   text: string;
   *   formId: string;
   * }>} event
   */
  onFoilingSetHandler (event) {
    const { font, text, formId } = event.detail

    if (this.form.getAttribute('id') !== formId) {
      return
    }

    const {
      'foiling-text': foilingTextInput,
      'foiling-font': foilingFontInput,
    } = this.els

    if (!(foilingTextInput && foilingFontInput)) {
      return
    }

    foilingTextInput.element.value = text
    foilingFontInput.element.value = font
  }

  /**
   * @param {CustomEvent<{
   *   formId: string;
   *   quantity: number;
   * }>} event
   */
  onExternalQuantityChange (event) {
    const { formId, quantity } = event.detail

    if (this.form.getAttribute('id') !== formId) {
      return
    }

    /**
     * @note selecting the input instead of quantity-adjuster
     * prevents an 'input' event being dispatched
     */
    const quantityAdjuster = getElement('quantity-adjuster input', {
      context: this.form,
    })
    const quantityAdjusterEl = getElement('quantity-adjuster', {
      context: this.form,
    })

    if (this.externalEls['single-pair-selector']) {
      this.externalEls['single-pair-selector'].elements.forEach(selector => {
        selector.setValue(quantity)
      })
    }

    if (String(quantity) === quantityAdjuster.value) {
      return
    }

    quantityAdjuster.value = quantity
    /**
     * @note keeps the quantity-adjuster element's own value attribute
     * (read by its `value` getter/setter) in sync with the input we
     * just wrote directly — otherwise anything reading quantity via
     * the element itself sees stale data
     */
    quantityAdjusterEl.setAttribute('value', quantity.toString())
  }

  async onSubmitHandler (e) {
    e.preventDefault()

    await this.addToCart()
  }

  async addToCart () {
    const postVariantInput = this.form.querySelector(
      '[data-product-post-el="variant-id"]',
    )
    if (postVariantInput && !postVariantInput.value) {
      return
    }

    const formData = new FormData(this.form)

    if (
      postVariantInput &&
      formData.get('id') &&
      !formData.get('items[0][id]')
    ) {
      const mainId = formData.get('id')
      const mainQty = formData.get('quantity') || '1'
      formData.delete('id')
      formData.delete('quantity')
      formData.set('items[0][id]', mainId)
      formData.set('items[0][quantity]', mainQty)
    }

    if (postVariantInput?.value) {
      const pairId = crypto.randomUUID()
      formData.set('items[0][properties][_post_pair_id]', pairId)
      formData.set('items[1][properties][_post_pair_id]', pairId)
      formData.set('items[1][properties][_post_child]', 'true')

      // Each earring needs its own post, so keep the post line quantity in
      // step with the earring quantity (e.g. a "Pair" = 2 earrings + 2 posts).
      const mainQty =
        formData.get('items[0][quantity]') || formData.get('quantity')
      if (mainQty) {
        formData.set('items[1][quantity]', mainQty)
      }
    }

    if (formData.get('charm_ids')) {
      const [nextIndexKey] = [...formData.entries()]
        .reverse()
        .find(([key]) => key.startsWith('items['))
      const index =
        Number(nextIndexKey.split('][')[0].replace('items[', '')) + 1

      const charmIds = formData.get('charm_ids').split(',')
      const charmProductIds = formData.get('charm_product_ids').split(',')
      const trimmedIds = [...new Set(charmIds)]

      let charmEngravingTexts = []
      if (formData.get('charm_engraving_texts') !== 'undefined') {
        charmEngravingTexts = JSON.parse(
          `[${formData.get('charm_engraving_texts') || ''}]`,
        )
      }

      const charmEngravingFonts = formData
        .get('charm_engraving_fonts')
        .split(',')

      trimmedIds.forEach((id, i) => {
        formData.set(`items[${index + i}][id]`, id)
        formData.set(
          `items[${index + i}][product_id]`,
          charmProductIds[charmIds.findIndex(charmID => charmID === id)],
        )
        formData.set(
          `items[${i + 1}][quantity]`,
          charmIds.filter(charmID => charmID === id).length,
        )
        formData.set(`items[${index + i}][properties][_charms_added]`, 'true')

        if (
          charmEngravingTexts.length > 0 &&
          charmEngravingTexts[i] !== 'undefined'
        ) {
          const engravingText = charmEngravingTexts[i]
          engravingText.forEach(element => {
            formData.set(
              `items[${i + 1}][properties][Engraving_${element[0]}]`,
              element[1],
            )
          })
        }

        if (charmEngravingFonts[i] !== 'undefined') {
          formData.set(
            `items[${index + i}][properties][_engraving_font]`,
            charmEngravingFonts[i],
          )
        }
      })
    }

    if (formData.get('connector_ids')) {
      const [lastItemKey] = [...formData.entries()]
        .reverse()
        .find(([key]) => key.startsWith('items['))
      const connectorIndex =
        Number(lastItemKey.split('][')[0].replace('items[', '')) + 1

      const connectorIds = formData.get('connector_ids').split(',')
      const connectorProductIds = formData
        .get('connector_product_ids')
        .split(',')

      ;[...new Set(connectorIds)].forEach((id, i) => {
        formData.set(`items[${connectorIndex + i}][id]`, id)
        formData.set(
          `items[${connectorIndex + i}][product_id]`,
          connectorProductIds[
            connectorIds.findIndex(connectorId => connectorId === id)
          ],
        )
        formData.set(
          `items[${connectorIndex + i}][quantity]`,
          connectorIds.filter(connectorId => connectorId === id).length,
        )
        // Behaves like a charm line in the cart, with a marker to tell them apart
        formData.set(
          `items[${connectorIndex + i}][properties][_charms_added]`,
          'true',
        )
        formData.set(
          `items[${connectorIndex + i}][properties][_connector]`,
          'true',
        )
      })
    }

    const result = await window.BAO.Cart.addItemFromForm(formData)

    // Customised variant charms product reset
    if (result && document.querySelectorAll('.prd-VariantCharms').length > 0) {
      document
        .querySelector('[data-product-charms-external-el="clearAll"]')
        ?.click() // clear selected charms and connectors
    }

    // Customised engraving product reset
    if (result && document.querySelector('#clear-engraving')) {
      document.querySelector('#clear-engraving').click() // clear selected options
    }

    // Customised foiled product reset
    if (result && document.querySelector('#clear-foiling')) {
      document.querySelector('#clear-foiling').click() // clear selected options
    }
  }

  /**
   * @returns {HTMLFormElement|null}
   */
  get form () {
    return this.querySelector('form')
  }

  get id () {
    return this.getAttribute('id') || this.form.getAttribute('id')
  }
}

customElements.define('product-form', ProductForm)
